import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import { toast } from 'sonner';
import {
  DEFAULT_PX_PER_M, DEFAULT_ROOM_W_M, DEFAULT_ROOM_H_M,
  ROOM_PADDING_M, getTablePxDims, TABLE_DIMS_M,
} from './seatUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PlanContext = createContext(null);

export function PlanProvider({ planToken, authToken, children }) {
  const [planData,    setPlanData]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [localPos,    setLocalPos]    = useState({});
  const [selectedId,  setSelectedId]  = useState(null);
  const [assignMode,  setAssignMode]  = useState(null);
  const [zoom,        setZoom]        = useState(1.0);
  const [snapToGrid,  setSnapToGrid]  = useState(true);   // snap 0.5m
  const [showDistances, setShowDistances] = useState(true);
  const [dragInfo,    setDragInfo]    = useState(null);   // {x, y, distToNearestM}

  const headers = useCallback(() => ({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  }), [authToken]);

  // ── Lecture du plan ─────────────────────────────────────────────────────────
  const fetchPlan = useCallback(async () => {
    try {
      const res  = await fetch(`${BACKEND}/api/plan-de-table/${planToken}`);
      const data = await res.json();
      setPlanData(data.plan);
      setLocalPos({});
    } catch { /* silent */ }
    setLoading(false);
  }, [planToken]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // ── Dimensions de la salle ───────────────────────────────────────────────────
  const roomWm    = planData?.room_width_m   ?? DEFAULT_ROOM_W_M;
  const roomHm    = planData?.room_height_m  ?? DEFAULT_ROOM_H_M;
  const pxPerM    = planData?.px_per_m       ?? DEFAULT_PX_PER_M;

  // Taille totale du canvas (salle + marge autour)
  const canvasW   = Math.round((roomWm  + ROOM_PADDING_M * 2) * pxPerM);
  const canvasH   = Math.round((roomHm  + ROOM_PADDING_M * 2) * pxPerM);
  // Origine de la salle dans le canvas (en pixels)
  const roomX0    = Math.round(ROOM_PADDING_M * pxPerM);
  const roomY0    = Math.round(ROOM_PADDING_M * pxPerM);
  const roomPxW   = Math.round(roomWm  * pxPerM);
  const roomPxH   = Math.round(roomHm  * pxPerM);

  // Snap à la grille (0.5m)
  const snapPx = useCallback((px) => {
    if (!snapToGrid) return px;
    const snapM = 0.5;  // snap tous les 0.5m
    const snapPxVal = snapM * pxPerM;
    return Math.round(px / snapPxVal) * snapPxVal;
  }, [snapToGrid, pxPerM]);

  // Sauvegarde room config
  const updateRoomConfig = useCallback(async (config) => {
    try {
      await fetch(`${BACKEND}/api/plan-de-table/${planToken}`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify(config),
      });
      setPlanData(prev => prev ? { ...prev, ...config } : prev);
    } catch { toast.error('Erreur sauvegarde salle'); }
  }, [planToken, headers]);

  // ── Positions optimistes (drag) ─────────────────────────────────────────────
  const setOptimisticPos = useCallback((id, x, y) => {
    setLocalPos(p => ({ ...p, [id]: { x, y } }));
  }, []);

  const commitPosition = useCallback(async (id, x, y) => {
    setLocalPos(p => { const n = { ...p }; delete n[id]; return n; });
    try {
      await fetch(`${BACKEND}/api/plan-de-table/${planToken}/tables/${id}`,
        { method: 'PATCH', headers: headers(), body: JSON.stringify({ x, y }) });
      setPlanData(prev => prev ? {
        ...prev,
        tables: prev.tables.map(t => t.id === id ? { ...t, x, y } : t),
      } : prev);
    } catch { toast.error('Erreur déplacement table'); }
  }, [planToken, headers]);

  // ── Tables ─────────────────────────────────────────────────────────────────
  const addTable = useCallback(async (tableData) => {
    try {
      const res = await fetch(`${BACKEND}/api/plan-de-table/${planToken}/tables`,
        { method: 'POST', headers: headers(), body: JSON.stringify(tableData) });
      if (!res.ok) throw new Error();
      await fetchPlan();
    } catch { toast.error('Erreur ajout table'); }
  }, [planToken, headers, fetchPlan]);

  const updateTable = useCallback(async (id, patch) => {
    try {
      await fetch(`${BACKEND}/api/plan-de-table/${planToken}/tables/${id}`,
        { method: 'PATCH', headers: headers(), body: JSON.stringify(patch) });
      setPlanData(prev => prev ? {
        ...prev,
        tables: prev.tables.map(t => t.id === id ? { ...t, ...patch } : t),
      } : prev);
    } catch { toast.error('Erreur modification table'); }
  }, [planToken, headers]);

  const deleteTable = useCallback(async (id) => {
    try {
      await fetch(`${BACKEND}/api/plan-de-table/${planToken}/tables/${id}`,
        { method: 'DELETE', headers: headers() });
      await fetchPlan();
      setSelectedId(null);
    } catch { toast.error('Erreur suppression'); }
  }, [planToken, headers, fetchPlan]);

  // ── Invités ─────────────────────────────────────────────────────────────────
  const assignSeat = useCallback(async (guestId, tableId, seatIdx) => {
    try {
      const res = await fetch(
        `${BACKEND}/api/plan-de-table/${planToken}/invites/${guestId}/assign`,
        { method: 'POST', headers: headers(),
          body: JSON.stringify({ table_id: tableId, seat_idx: seatIdx }) });
      if (!res.ok) throw new Error();
      await fetchPlan();
      setAssignMode(null);
    } catch { toast.error('Erreur assignation'); }
  }, [planToken, headers, fetchPlan]);

  const unassignSeat = useCallback(async (guestId) => {
    try {
      await fetch(
        `${BACKEND}/api/plan-de-table/${planToken}/invites/${guestId}/assign`,
        { method: 'DELETE', headers: headers() });
      await fetchPlan();
    } catch { toast.error('Erreur désassignation'); }
  }, [planToken, headers, fetchPlan]);

  // ── Zoom ─────────────────────────────────────────────────────────────────────
  const changeZoom = useCallback((delta) => {
    setZoom(z => Math.min(3.0, Math.max(0.2, Math.round((z + delta) * 10) / 10)));
  }, []);
  const resetZoom = useCallback(() => setZoom(1.0), []);
  const fitToScreen = useCallback(() => {
    // Calculer le zoom pour que la salle tienne dans la fenêtre
    const vw = window.innerWidth  - 320; // soustrait les panneaux
    const vh = window.innerHeight - 80;
    const zw = vw / canvasW;
    const zh = vh / canvasH;
    setZoom(Math.min(zw, zh, 1.5));
  }, [canvasW, canvasH]);

  // ── Tables résolues avec positions optimistes ─────────────────────────────────
  const resolvedTables = planData?.tables?.map(t => ({
    ...t,
    ...(localPos[t.id] || {}),
  })) ?? [];

  const invites   = planData?.invites  ?? [];
  const groups    = planData?.groups   ?? [];
  const segments  = planData?.segments ?? [];
  const mainSeg   = segments.find(s => s.has_plan) || segments[0];

  return (
    <PlanContext.Provider value={{
      planData, loading, planToken,
      resolvedTables, invites, groups, segments, mainSeg,
      selectedId, setSelectedId,
      assignMode, setAssignMode,
      zoom, changeZoom, resetZoom, fitToScreen,
      snapToGrid, setSnapToGrid,
      showDistances, setShowDistances,
      dragInfo, setDragInfo,
      // Room dimensions
      roomWm, roomHm, pxPerM,
      canvasW, canvasH,
      roomX0, roomY0, roomPxW, roomPxH,
      snapPx,
      updateRoomConfig,
      // CRUD
      setOptimisticPos, commitPosition,
      addTable, updateTable, deleteTable,
      assignSeat, unassignSeat,
      fetchPlan,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used inside PlanProvider');
  return ctx;
}
